/**
 * SuperMemo SM-2 Spaced Repetition Algorithm Implementation
 * 
 * Score levels:
 * 1: Again (Complete failure / incorrect)
 * 3: Hard (Correct with significant hesitation/difficulty)
 * 4: Good (Correct with normal effort)
 * 5: Easy (Perfect recall / immediate response)
 */

export const QUALITY_SCORES = {
  AGAIN: 1,
  HARD: 3,
  GOOD: 4,
  EASY: 5,
};

/**
 * Calculates updated SM-2 parameters for a card after a review.
 * 
 * @param {Object} currentSrs - Existing SRS state: { interval, repetition, easeFactor }
 * @param {number} qualityScore - Rating from 1 (Again) to 5 (Easy)
 * @returns {Object} Updated SRS state with new interval, repetition, easeFactor, and dueDate
 */
export function calculateSM2(currentSrs = {}, qualityScore = QUALITY_SCORES.GOOD) {
  let interval = currentSrs.interval || 0;
  let repetition = currentSrs.repetition || 0;
  let easeFactor = currentSrs.easeFactor || 2.5;

  const score = Math.max(1, Math.min(5, Number(qualityScore) || 3));

  if (score < 3) {
    // Failed recall: reset repetitions and set interval to 1 day
    repetition = 0;
    interval = 1;
  } else {
    // Successful recall
    if (repetition === 0) {
      interval = 1;
    } else if (repetition === 1) {
      interval = 6;
    } else {
      interval = Math.round(interval * easeFactor);
    }
    repetition += 1;
  }

  // Update Ease Factor (EF)
  // EF' = EF + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02))
  easeFactor = easeFactor + (0.1 - (5 - score) * (0.08 + (5 - score) * 0.02));
  if (easeFactor < 1.3) {
    easeFactor = 1.3;
  }

  const now = new Date();
  const nextDueDate = new Date(now.getTime() + interval * 24 * 60 * 60 * 1000);

  return {
    interval,
    repetition,
    easeFactor: Number(easeFactor.toFixed(2)),
    dueDate: nextDueDate.toISOString(),
    lastReviewed: now.toISOString(),
  };
}

/**
 * Checks if a card is due for review today.
 */
export function isCardDue(card) {
  if (!card?.srs?.dueDate) return true;
  return new Date(card.srs.dueDate) <= new Date();
}

/**
 * Filters decks or card arrays for items due today.
 */
export function getDueCards(cards = []) {
  return cards.filter(isCardDue);
}

/**
 * Helper to calculate stats for a deck of cards.
 */
export function getDeckStats(cards = []) {
  let total = cards.length;
  let dueCount = 0;
  let masteredCount = 0; // Interval >= 21 days
  let learningCount = 0; // Interval < 21 days

  cards.forEach((card) => {
    if (isCardDue(card)) dueCount++;
    if ((card.srs?.interval || 0) >= 21) {
      masteredCount++;
    } else {
      learningCount++;
    }
  });

  return { total, dueCount, masteredCount, learningCount };
}

